// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Button from "themeComponents/Button";
import Typography from "themeComponents/Typography";

// Authentication layout components
import FormikInput from "components/formikInput";
import api from "configs/api";
import { Form, Formik, FormikHelpers } from "formik";
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";
import * as Yup from "yup";
import { useContext, useState } from "react";
import { AppContext } from "context/appContext";
import { useDispatch } from "react-redux";
import { login } from "slices/authSlice";
import useAuth from "hooks/useAuth";

interface LoginValues {
  email: string;
  password: string;
}

function Illustration(): JSX.Element {
  const navigator = useNavigate();
  const { deviceInfo } = useAuth();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const { showAlert } = useContext(AppContext);
  const domainName = window.location.hostname;

  const handleSubmit = async (values: LoginValues): Promise<void> => {
    try {
      setLoading(true);
      const response = await api.post(`auth/user/login`, { ...values, domainName, deviceInfo });
      if (response.data.status) {
        // Backend returns: { status, token, user, message }
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("token", response.data.token);
        
        dispatch(login({
          token: response.data.token,
          user: response.data.user,
          subdomain: response.data.subdomainData || null
        }));
        
        navigator(`/dashboard`);
        showAlert('Login successful!', 'success', 'Welcome');
      } else {
        showAlert(response.data.message || 'Login failed', 'error', 'Login Error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Please try again.';
      showAlert(errorMessage, 'error', 'Login Error');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IllustrationLayout>
      <Formik
        initialValues={{ email: "", password: "" }}
        validationSchema={Yup.object({
          email: Yup.string().email("Invalid email").required("Email is required"),
          password: Yup.string().required("Password is required"),
        })}
        onSubmit={async (values: LoginValues, { setSubmitting }: FormikHelpers<LoginValues>) => {
          await handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Box mb={2}>
              <FormikInput name="email" type="email" placeholder="Email" size="large" />
            </Box>
            <Box mb={2}>
              <FormikInput name="password" type="password" placeholder="Password" size="large" />
            </Box>
            <Box mt={4} mb={1}>
              <Button loading={loading} color="info" size="large" fullWidth type="submit" disabled={isSubmitting}>
                Sign In
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
      {domainName === (process.env.REACT_APP_CLIENT_DOMAIN || '') && (
        <Box mt={3} textAlign="center">
          <Typography variant="button" color="text" fontWeight="regular">
            Don&apos;t have an account?{" "}
            <Typography
              component={Link}
              to="/authentication/sign-up"
              variant="button"
              color="info"
              fontWeight="medium"
            >
              Sign up
            </Typography>
          </Typography>
        </Box>
      )}
    </IllustrationLayout>
  );
}

export default Illustration;

